/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel, ROW_DIRTY_MARKER_CLASS, ROW_GROUP_CLASS } from '@gh/common-internal-shared';
import { getRouteInitData, setEntityField, whenModified } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { validationError } from '@gh/core-ui';
import {
  buildEditableCheckboxColumn,
  buildEditableNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { flatMap } from 'lodash';
import {
  calculateThresholdOverlappedRanges,
  getReportFyaNumberType,
  getReportFyaThresholdId, hasThresholdRangeFor,
  isReportFyaThresholdGroup,
  isReportFyaThresholdRegular,
  ReportFyaThreshold,
  reportFyaThresholdArr,
  reportFyaThresholdFieldState,
  validateReportFyaThresholdField,
} from '../../../resources/report-fya-details.model';
import { ReportFyaDetailsCommandService } from '../report-fya-details-command.service';
import { ReportFyaDetailsFormState } from '../report-fya-details-form.service';

@Component({
  selector: 'gh-report-fya-index-values-tab',
  template: require('./report-fya-index-values-tab.component.html'),
  styles: [require('./report-fya-index-values-tab.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportFyaIndexValuesTabComponent implements OnInit {
  definition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private commands: ReportFyaDetailsCommandService,
              public form: ReportFyaDetailsFormState) {
  }

  ngOnInit(): void {
    this.definition = this.buildGridDefinition();
  }

  onFocusedCellChange(): void {
    this.commands.resetFocusedCell();
  }

  private buildGridDefinition(): GridDefinition {
    const { financialIndexDict } = getRouteInitData(this.route);
    const model = createDefaultGridModel({
      getId: getReportFyaThresholdId,
      getCellDef: reportFyaThresholdFieldState,
      validate: (entity, field, value) => {
        // run default validation
        const errors = validateReportFyaThresholdField(entity, field, value);
        if (isNotNil(errors)) {
          return errors;
        }
        // validate if we have overlapping values
        const overlappedRanges = calculateThresholdOverlappedRanges(
          reportFyaThresholdArr.update(
            this.form.thresholds,
            getReportFyaThresholdId(entity),
            setEntityField(entity, <any>field, value)),
          entity.type);
        return hasThresholdRangeFor(overlappedRanges, entity, field) ?
          { overlap: validationError('msg_index_values_overlap') }
          : void 0;
      },
      command: (threshold: ReportFyaThreshold, name: string, value: any) =>
        this.commands.setThresholdField(threshold, name, value),
    });
    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [
        {
          field: 'type',
          headerName: this.translations.get('lbl_index_name'),
          width: 250,
          valueFormatter: ({ value, data }) => {
            if (isReportFyaThresholdGroup(data)) {
              return this.translations.get(data.groupType);
            } else if (data.index === 1) {
              return financialIndexDict.get(value);
            } else {
              return '';
            }
          },
          rowSpan: ({ data }) => data.index === 1 ? 2 : 1, // tslint:disable-line:no-magic-numbers
          cellClassRules: {
            'm-index-name': ({ data }: { data: ReportFyaThreshold }) => data.index === 1,
            'text-uppercase': ({ data }: { data: ReportFyaThreshold }) => isReportFyaThresholdGroup(data),
          },
        },
        {
          field: 'index',
          headerName: '',
          width: 15,
          suppressNavigable: true,
        },
        ...flatMap(['bad', 'ok', 'good'], (quality) => {
          const cellClassRules = {
            [`m-${quality}-value`]: ({ data }: { data: ReportFyaThreshold }) => isReportFyaThresholdRegular(data),
          };

          return [
            {
              field: 'gapStart',
              headerName: '',
              headerClass: 'm-gap-start',
              width: 15,
              suppressNavigable: true,
            },
            {
              field: `${quality}FromInclude`,
              headerName: this.translations.get('lbl_equal'),
              headerClass: 'm-value-header-cell',
              cellClassRules,
              width: 45,
              ...buildEditableCheckboxColumn(model.getCellAccessor(`${quality}FromInclude`)),
            },
            {
              field: `${quality}From`,
              headerName: this.translations.get('lbl_greater_than'),
              headerClass: 'm-value-header-cell',
              cellClassRules,
              width: 100,
              ...buildEditableNumberColumn(getReportFyaNumberType, model.getCellAccessor(`${quality}From`)),
            },
            {
              field: `${quality}ToInclude`,
              headerName: this.translations.get('lbl_equal'),
              headerClass: 'm-value-header-cell',
              cellClassRules,
              width: 45,
              ...buildEditableCheckboxColumn(model.getCellAccessor(`${quality}ToInclude`)),
            },
            {
              field: `${quality}To`,
              headerName: this.translations.get('lbl_less_than'),
              headerClass: 'm-value-header-cell',
              cellClassRules,
              width: 100,
              ...buildEditableNumberColumn(getReportFyaNumberType, model.getCellAccessor(`${quality}To`)),
            },
            {
              field: 'gapEnd',
              width: 15,
              headerName: '',
              suppressNavigable: true,
            },
          ];
        }),
      ],
      options: {
        // suppressRowTransform is necesary for rowSpan to work
        suppressRowTransform: true,
        headerHeight: 50,
        defaultColDef: {
          sortable: false,
          resizable: false,
        },
        rowClassRules: {
          [ROW_GROUP_CLASS]: ({ data }) => isReportFyaThresholdGroup(data),
          [ROW_DIRTY_MARKER_CLASS]: ({ data }) => whenModified(data),
          ['row-separation-line']: ({ data }) => data.index === 2, // tslint:disable-line:no-magic-numbers
        },
      },
    });
  }
}
